import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import ChatAnswer from "./chatAnswer/index";
import ComposeDialog from "src/components/compose/components/dialog";
import { invalidateSeekEmailResponse } from "src/redux/actions/seek";

import styles from "./chat.module.scss";
import cx from "classnames";
import _ from "lodash";
import { parseEmailSubjectAndBody } from "src/utils/seekEmailCompose.helper";

interface SeekChatProps {
  sessionView: boolean;
  seekAsset?: boolean;
  seekAnswer?: string | null;
}

const SeekChat: React.FC<SeekChatProps> = ({ sessionView, seekAsset , seekAnswer}) => {
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const seekEmailResponse = useSelector((state: any) => {
    const body = _.get(state,"seek.email.body");
    if(!body){
      return body;
    }

    const { subject, body: extractedBody  } = parseEmailSubjectAndBody(
      `${body}
        <p>&nbsp;</p>
      <div class="mceNonEditable widget" data-widget-id="assets.grid">
        {{> AssetAttachments}}
      </div>`
    );

    return {
      ...(_.size(subject) > 0 && {subject: subject}),
      body: extractedBody,
    }
  });

  const profile = useSelector((state: any) => state.users.me);
  const session = useSelector((state: any) => state.seek.session);
  const chats = useSelector((state: any) => state.seek.session.item.chats);
  const sessionLoading = useSelector((state: any) =>
    _.get(state, "seek.session.loading", false),
  );
  const [completedChats, setCompletedChats] = useState<{
    [key: number]: boolean;
  }>({});

  const [showCompose, setShowCompose] = useState(false);
  const [assetsForCompose, setAssetsForCompose] = useState<any[] | null>([]);
  
  const seekChats = useMemo(() => {
    if(_.size(chats) > 0) {
      return chats;
    } else if(_.get(session, "item.id") && !sessionLoading){
      return [
        {
          id: 1,
          question: "",
          answer: "No chats available for this session.",
          citations: [],
          contents: [],
          helpful: null,
        },
      ];
    } else {
      return [];
    }
  }, [chats]);

  const handleComplete = useCallback((index: number) => {
    setCompletedChats((prev) => ({ ...prev, [index]: true }));
  }, []); 

  const handleDiscardEmail = () => {
    setShowCompose(false);
    dispatch(invalidateSeekEmailResponse());
  };

  const scrollToBottom = () => {
    if (chatContainerRef && chatContainerRef.current) {
      const scrollTop = _.floor(chatContainerRef.current.scrollTop);
      const scrollHeight = _.floor(chatContainerRef.current.scrollHeight);
      const clientHeight = _.floor(chatContainerRef.current.clientHeight);
      if (scrollTop + clientHeight >= scrollHeight) {
        return;
      }
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    if (seekAnswer ) {
      const lastIndex = seekChats.length - 1;
      setCompletedChats(prev => ({ ...prev, [lastIndex]: true }));
    }
  }, [seekAnswer, seekChats]);

  return (
    <div
      ref={chatContainerRef}
      className={cx(
        styles.chatContainer,
        sessionView && styles.sessionViewChatContainer,
        seekAsset && styles.seekAssetChatContainer,
      )}
    >
      {seekChats.map((chat: any, index: number) => (
        <React.Fragment key={index}>
          <ChatAnswer
            setAssetsForCompose={setAssetsForCompose}
            setShowCompose={setShowCompose}
            index={index}
            chat={chat}
            profile={profile}
            handleComplete={handleComplete}
            skipTypewriting={!!completedChats[index]}
            showDivider={seekChats.length > 1}
            seekAsset={seekAsset}
            scrollToBottom={scrollToBottom}
          />
        </React.Fragment>
      ))}
      {sessionLoading && _.size(seekChats) === 0 && (
        <span className={styles.loading}>Loading...</span>
      )}

      <ComposeDialog
        show={showCompose}
        dialog={true}
        sourceType="Share Asset"
        initialContent={assetsForCompose}
        emailBody={seekEmailResponse?.body ?? ""}
        emailSubject={seekEmailResponse?.subject ?? ""}
        onClose={() => handleDiscardEmail()}
        onFinish={() => handleDiscardEmail()}
      />

    </div>
  );
};

export default SeekChat;
